/**
 * Carte résumé d'une garantie
 */

import { useNavigate } from 'react-router-dom';
import { Power, PowerOff } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WARRANTY_TYPES, type Warranty, type WarrantyListItem } from '../types/warranty.types';
import {
  getWarrantyTypeLabel,
  getVehicleTypeLabel,
  getDurationLabel,
} from '../utils/warrantyFormatter';
import { calculateMonthlyBreakdown } from '../services/pricingCalculator';
import { Can } from '@/Components/auth/Can';
import { useUpdateWarranty } from '../hooks/useWarrantyMutations';
import { ColorPickerPopover } from './ColorPickerPopover';
import { AssistanceBadge } from '@/features/assistance/components/AssistanceBadge';
import { ASSISTANCE_LABEL } from '@/features/assistance/assistanceWording';

interface WarrantyCardProps {
  warranty: Warranty | WarrantyListItem;
  showActions?: boolean;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onToggleActive?: () => void;
  onCardClick?: (uuid: string) => void;
  currentUserType?: 'INTERNAL' | 'EXTERNAL';
  isDragEnabled?: boolean;
  showTransferPrices?: boolean;
  isWarrantyPolicyOwner?: boolean;
  hideMonthlyPrice?: boolean;
  /**
   * Afficher la ligne d'assistance, Oui OU Non.
   *
   * Inconditionnelle une fois vraie : une ligne qui n'apparaît que sur les garanties
   * qui portent une formule donne des vignettes à huit lignes et d'autres à neuf,
   * donc des prix et des boutons qui ne s'alignent plus d'une carte à l'autre.
   *
   * Même condition que la colonne du tableau, pour que les deux vues disent la même
   * chose : l'offre est visible pour la société affichée, ET au moins une garantie du
   * périmètre en porte une.
   */
  showAssistance?: boolean;
}

export const WarrantyCard = ({
  warranty,
  showActions = true,
  onDuplicate,
  onToggleActive,
  onCardClick,
  currentUserType,
  isDragEnabled = true,
  showTransferPrices = true,
  isWarrantyPolicyOwner = true,
  hideMonthlyPrice = false,
  showAssistance = false,
}: WarrantyCardProps) => {
  const navigate = useNavigate();
  const updateWarranty = useUpdateWarranty();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // `disabled` booléen = draggable ET droppable désactivés (cf.
    // normalizeDisabled). Volontaire pour les garanties inactives : la liste
    // trie `IS_ACTIVE DESC` en critère primaire, donc une carte active déposée
    // dans le bloc inactif verrait son ordre changer en base sans bouger à
    // l'écran. dnd-kit exclut les droppables désactivés de la détection de
    // collision (`enabledDroppableContainers`), si bien que survoler une carte
    // inactive vise la carte active la plus proche — le dépôt reste utile.
    id: warranty.uuid,
    disabled: !isDragEnabled || !warranty.isActive,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const societyName = 'societyName' in warranty ? warranty.societyName : warranty.society?.name;
  const warrantyId = warranty.uuid;

  const primaryColor = warranty.color || '#8074af';
  const lightColor = `${primaryColor}33`;
  const mediumColor = `${primaryColor}dd`;

  const hasCustomColor = warranty.color && /^#[0-9A-Fa-f]{6}$/.test(warranty.color);
  const headerColor = hasCustomColor
    ? ''
    : 'tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7]';
  const headerStyle = hasCustomColor
    ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${mediumColor} 100%)` }
    : {};

  const handleCardClick = () => {
    if (isDragging) return;
    if (onCardClick) {
      onCardClick(warrantyId);
    } else {
      navigate(`/warranties/${warrantyId}`);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleColorChange = (color: string | null) => {
    updateWarranty.mutate({
      id: warranty.uuid,
      warranty: { color },
    });
  };

  const isInactive = !warranty.isActive;

  const dragProps = isDragEnabled && warranty.isActive
    ? { ...listeners, ...attributes }
    : {};

  const cardContent = (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      {...dragProps}
      className={`tw-bg-white tw-rounded-xl tw-shadow-md tw-overflow-hidden tw-transition-all tw-border-2 tw-border-gray-200 hover:tw-shadow-lg hover:tw-border-gray-300 tw-cursor-pointer tw-relative tw-select-none ${isDragEnabled && warranty.isActive ? 'tw-cursor-grab active:tw-cursor-grabbing' : ''} ${isInactive ? 'tw-opacity-60' : ''} ${isDragging ? 'tw-z-50' : ''}`}
    >
      {isInactive && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none tw-z-10">
          <div className="tw-text-gray-400 tw-font-bold tw-text-6xl tw-opacity-30 tw-rotate-[-25deg]">
            INACTIVE
          </div>
        </div>
      )}
      {/*
        Hauteur calée sur DEUX lignes de titre (2 × 20 px + py-3), et titre borné à
        deux lignes : « 6 MOIS MOTEUR BOITE PONT » passait à la ligne quand
        « 12 MOIS ESSENTIEL » tenait sur une, et tout le contenu dessous — prix
        compris — se décalait d'une vignette à l'autre.
      */}
      <div className={`${headerColor} tw-py-3 tw-px-4 tw-min-h-[64px] tw-flex tw-items-center tw-justify-center tw-relative ${isInactive ? 'tw-grayscale' : ''}`} style={headerStyle}>
        <h3 className="tw-text-white tw-font-bold tw-text-center tw-leading-tight tw-text-base tw-mb-0 tw-line-clamp-2" title={warranty.name}>{warranty.name}</h3>
      </div>

      <div className="tw-text-center tw-border-b tw-border-gray-100 tw-py-4 tw-px-4 tw-min-h-[90px] tw-flex tw-flex-col tw-justify-center">
        {'isCashPayment' in warranty && 'isMensualisable' in warranty && 'monthlyPlateformFees' in warranty ? (
          <>
            {warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && warranty.isCashPayment && warranty.isMensualisable && !hideMonthlyPrice ? (
              <>
                <div className="tw-font-bold tw-text-3xl" style={{ color: primaryColor }}>
                  {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
                  <span className="tw-text-xl" style={{ color: primaryColor }}>€</span>
                  <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
                </div>
                <div className="tw-text-gray-500 tw-mt-1 tw-text-xs">
                  {calculateMonthlyBreakdown(warranty.sellingPrice, warranty.duration, warranty.monthlyPlateformFees).monthlyClient.toFixed(2)} <span style={{ color: primaryColor }}>€</span> × {warranty.duration} mois
                </div>
              </>
            ) : warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && !warranty.isCashPayment && warranty.isMensualisable ? (
              <>
                <div className="tw-font-bold tw-text-3xl" style={{ color: primaryColor }}>
                  {warranty.sellingPrice.toFixed(2)} <span className="tw-text-xl" style={{ color: primaryColor }}>€</span>
                  <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
                </div>
                <div className="tw-text-gray-500 tw-mt-1 tw-text-xs">
                  × {warranty.duration} mois
                </div>
              </>
            ) : (
              <div className="tw-font-bold tw-text-3xl" style={{ color: primaryColor }}>
                {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
                <span className="tw-text-xl" style={{ color: primaryColor }}>€</span>
                <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="tw-font-bold tw-text-gray-900 tw-text-4xl">
              {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
              <span className="tw-text-gray-500 tw-text-xl">€</span>
            </div>
            <div className="tw-text-gray-500 tw-mt-0.5 tw-text-xs">/TTC</div>
          </>
        )}
      </div>

      <div className="tw-space-y-2 tw-py-4 tw-px-4">
        {currentUserType === 'INTERNAL' && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Société : <strong>{societyName}</strong>
              </span>
            </div>
          </div>
        )}

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
            <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Type : <strong>{getWarrantyTypeLabel(warranty.warrantyType)}</strong>
            </span>
          </div>
        </div>

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
            <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Véhicule : <strong>{getVehicleTypeLabel(warranty.vehicleType)}</strong>
            </span>
          </div>
        </div>

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
            <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Durée : <strong>{warranty.warrantyType === WARRANTY_TYPES.EXTENSION ? '—' : getDurationLabel(warranty.duration)}</strong>
            </span>
          </div>
        </div>

        {'kmLimit' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Limite KM : <strong>{warranty.kmLimit ? warranty.kmLimit.toLocaleString() : '—'}</strong>
              </span>
            </div>
          </div>
        )}

        {'financialCap' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Plafond : <strong>{warranty.financialCap.toLocaleString()} €</strong>
              </span>
            </div>
          </div>
        )}

        {showAssistance && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                {ASSISTANCE_LABEL} :{' '}
                <AssistanceBadge
                  hasAssistance={
                    'assistanceFormulaId' in warranty &&
                    warranty.assistanceFormulaId != null
                  }
                />
              </span>
            </div>
          </div>
        )}

        {'partsCount' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Pièces : <strong>{warranty.partsCount}</strong>
              </span>
            </div>
          </div>
        )}

        {showTransferPrices && 'headTransferPrice' in warranty && 'memberTransferPrice' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5" style={{ backgroundColor: lightColor }}>
              <span className="tw-rounded-full tw-w-1.5 tw-h-1.5" style={{ backgroundColor: primaryColor }}></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Rétro/Achat : <strong>
                  {warranty.headTransferPrice ? `${warranty.headTransferPrice.toFixed(2)} €` : '—'} / {warranty.memberTransferPrice ? `${warranty.memberTransferPrice.toFixed(2)} €` : '—'}
                </strong>
              </span>
            </div>
          </div>
        )}
      </div>

      {/*
        `flex-wrap` : en vignette la carte descend sous 200 px, où « Dupliquer » + le
        sélecteur de couleur + « Désactiver » réclament près de 280 px. Sans ça la
        barre débordait, et le `overflow-hidden` de la carte tranchait le dernier
        bouton en « Dés… » — un bouton coupé, sans le moindre signal.
      */}
      {showActions && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-4 tw-pb-4" onClick={handleButtonClick}>
          {onDuplicate && isWarrantyPolicyOwner && (
            <Can permission="warranty.duplicate">
              <button
                onClick={onDuplicate}
                className="tw-flex-1 tw-min-w-0 tw-px-3 tw-py-2 tw-text-xs tw-font-bold tw-text-white tw-rounded-lg tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-1 tw-shadow-md"
                style={{
                  backgroundColor: isInactive ? '#6b7280' : primaryColor,
                }}
                onMouseEnter={(e) => {
                  if (!isInactive) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                title="Dupliquer"
              >
                <svg
                  className="tw-w-4 tw-h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>Dupliquer</span>
              </button>
            </Can>
          )}

          {warranty.isActive && isWarrantyPolicyOwner && (
            <Can permission="warranty.edit_color">
              <ColorPickerPopover
                value={warranty.color}
                onChange={handleColorChange}
              />
            </Can>
          )}

          {onToggleActive && isWarrantyPolicyOwner && (
            <Can permission="warranty.toggle_status">
              <button
                onClick={onToggleActive}
                className={`tw-flex-1 tw-min-w-0 tw-px-3 tw-py-2 tw-text-xs tw-font-bold tw-text-white tw-rounded-lg tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-1 tw-shadow-md ${
                  warranty.isActive
                    ? 'tw-bg-orange-600 hover:tw-bg-orange-700'
                    : 'tw-bg-green-600 hover:tw-bg-green-700'
                }`}
                title={warranty.isActive ? 'Désactiver' : 'Activer'}
              >
                {warranty.isActive ? (
                  <PowerOff className="tw-w-3 tw-h-3" />
                ) : (
                  <Power className="tw-w-3 tw-h-3" />
                )}
                <span>{warranty.isActive ? 'Désactiver' : 'Activer'}</span>
              </button>
            </Can>
          )}
        </div>
      )}
    </div>
  );

  // Fallback without permission (no click, no drag)
  const fallbackContent = (
    <div className={`tw-bg-white tw-rounded-xl tw-shadow-md tw-overflow-hidden tw-transition-all tw-border-2 tw-border-gray-200 tw-relative ${isInactive ? 'tw-opacity-60' : ''}`}>
      {isInactive && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none tw-z-10">
          <div className="tw-text-gray-400 tw-font-bold tw-text-6xl tw-opacity-30 tw-rotate-[-25deg]">
            INACTIVE
          </div>
        </div>
      )}
      <div className={`${headerColor} tw-py-3 tw-px-4 tw-min-h-[64px] tw-flex tw-items-center tw-justify-center ${isInactive ? 'tw-grayscale' : ''}`} style={headerStyle}>
        <h3 className="tw-text-white tw-font-bold tw-text-center tw-leading-tight tw-text-base tw-mb-0 tw-line-clamp-2" title={warranty.name}>{warranty.name}</h3>
      </div>

      <div className="tw-text-center tw-border-b tw-border-gray-100 tw-py-4 tw-px-4 tw-min-h-[100px] tw-flex tw-flex-col tw-justify-center">
        {'isCashPayment' in warranty && 'isMensualisable' in warranty && 'monthlyPlateformFees' in warranty ? (
          <>
            {warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && warranty.isCashPayment && warranty.isMensualisable && !hideMonthlyPrice ? (
              <>
                <div className="tw-font-bold tw-text-green-600 tw-text-3xl">
                  {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
                  <span className="tw-text-green-600 tw-text-xl">€</span>
                  <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
                </div>
                <div className="tw-text-gray-500 tw-mt-1 tw-text-xs">
                  {calculateMonthlyBreakdown(warranty.sellingPrice, warranty.duration, warranty.monthlyPlateformFees).monthlyClient.toFixed(2)} <span className="tw-text-green-600">€</span> × {warranty.duration} mois
                </div>
              </>
            ) : warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && !warranty.isCashPayment && warranty.isMensualisable ? (
              <>
                <div className="tw-font-bold tw-text-green-600 tw-text-3xl">
                  {warranty.sellingPrice.toFixed(2)} <span className="tw-text-green-600 tw-text-xl">€</span>
                  <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
                </div>
                <div className="tw-text-gray-500 tw-mt-1 tw-text-xs">
                  × {warranty.duration} mois
                </div>
              </>
            ) : (
              <div className="tw-font-bold tw-text-green-600 tw-text-3xl">
                {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
                <span className="tw-text-green-600 tw-text-xl">€</span>
                <span className="tw-text-gray-500 tw-text-xs tw-ml-1">/TTC</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="tw-font-bold tw-text-gray-900 tw-text-4xl">
              {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
              <span className="tw-text-gray-500 tw-text-xl">€</span>
            </div>
            <div className="tw-text-gray-500 tw-mt-0.5 tw-text-xs">/TTC</div>
          </>
        )}
      </div>

      <div className="tw-space-y-2 tw-py-4 tw-px-4">
        {currentUserType === 'INTERNAL' && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Société : <strong>{societyName}</strong>
              </span>
            </div>
          </div>
        )}

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
            <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Type : <strong>{getWarrantyTypeLabel(warranty.warrantyType)}</strong>
            </span>
          </div>
        </div>

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
            <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Véhicule : <strong>{getVehicleTypeLabel(warranty.vehicleType)}</strong>
            </span>
          </div>
        </div>

        <div className="tw-flex tw-items-start tw-gap-2">
          <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
            <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
          </div>
          <div className="tw-flex-1">
            <span className="tw-text-gray-700 tw-text-xs">
              Durée : <strong>{warranty.warrantyType === WARRANTY_TYPES.EXTENSION ? '—' : getDurationLabel(warranty.duration)}</strong>
            </span>
          </div>
        </div>

        {'kmLimit' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Limite KM : <strong>{warranty.kmLimit ? warranty.kmLimit.toLocaleString() : '—'}</strong>
              </span>
            </div>
          </div>
        )}

        {'financialCap' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Plafond : <strong>{warranty.financialCap.toLocaleString()} €</strong>
              </span>
            </div>
          </div>
        )}

        {showAssistance && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                {ASSISTANCE_LABEL} :{' '}
                <AssistanceBadge
                  hasAssistance={
                    'assistanceFormulaId' in warranty &&
                    warranty.assistanceFormulaId != null
                  }
                />
              </span>
            </div>
          </div>
        )}

        {'partsCount' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Pièces : <strong>{warranty.partsCount}</strong>
              </span>
            </div>
          </div>
        )}

        {showTransferPrices && 'headTransferPrice' in warranty && 'memberTransferPrice' in warranty && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-rounded-full tw-bg-[#8074af]/20 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-w-4 tw-h-4 tw-mt-0.5">
              <span className="tw-rounded-full tw-bg-[#575fa7] tw-w-1.5 tw-h-1.5"></span>
            </div>
            <div className="tw-flex-1">
              <span className="tw-text-gray-700 tw-text-xs">
                Rétro/Achat : <strong>
                  {warranty.headTransferPrice ? `${warranty.headTransferPrice.toFixed(2)} €` : '—'} / {warranty.memberTransferPrice ? `${warranty.memberTransferPrice.toFixed(2)} €` : '—'}
                </strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Can
      permission="warranty.view"
      fallback={fallbackContent}
    >
      {cardContent}
    </Can>
  );
};
